/**
 * @ Author: Housefolios
 * @ Create Time: 2021-12-16 11:36:56
 * @ Modified by: David Helmick
 * @ Modified time: 2025-05-08 13:10:36
 * @ Description: Card containing the sign in fields and button
 */

import React from 'react'

import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined'

import {
  AuthCardHeader,
  AuthMobileLogo,
  AuthNavLink,
  AuthPageShell,
  AuthPrimaryButton,
  authBackdropSx,
  authCardSx,
  authCardWrapSx,
  authFlexSpacerSx,
  authFormErrorCardSx,
  authFormErrorIconSx,
  authFormErrorTextSx,
  authFormErrorWrapSx,
  authPasswordToggleAvatarSx,
  authTextEndSx,
} from '../authChrome'

// Centralized lodash imports - much cleaner!
import { isEmpty, get } from '@/utils/lodash'

import {
  Grid,
  Avatar,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  IconButton,
  Backdrop,
  CircularProgress,
  Box,
} from '@mui/material'

import housefoliosLogo from '@/assets/images/HousefoliosCloudTransparent.png'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import {
  EMAIL_MAX_LENGTH_MESSAGE,
  EMAIL_MAX_LENGTH_VALIDATOR,
} from '@/constants/validation'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'

import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import { saveAuthToken } from '@/utils/auth'
import useAffiliate from '@/hooks/useAffiliate'
import { useNavigate } from 'react-router'

const hostName = import.meta.env.VITE_HOSTNAME

const SIGN_IN = gql`
  mutation ($login: String!, $password: String!) {
    signIn(login: $login, password: $password) {
      token
      member
      reset
    }
  }
`

export default function SignInCardComponent(props) {
  function getCookie(cName) {
    const name = cName + '='
    const cDecoded = decodeURIComponent(document.cookie) //to be careful
    const cArr = cDecoded.split('; ')
    let res
    cArr.forEach((val) => {
      if (val.indexOf(name) === 0) res = val.substring(name.length)
    })
    return res
  }

  let navigate = useNavigate()

  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  let source = urlParams.get('source')
  let sourceCookie = getCookie('SourceSignUp')
  let setCookie = false
  let headerImage = housefoliosLogo
  const [{ affiliate, affiliateLoading, urlAffiliate, urlHelmetData }] =
    useAffiliate({
      memberValue:
        sourceCookie === 'null' ? source : sourceCookie || source,
    })
  // let result = useQuery(GET_AFFILIATE, {
  //   variables: {
  //     memberValue:
  //       sourceCookie === 'null' ? source : sourceCookie || source,
  //   },
  // })
  // const affiliateLoading = result.loading
  // const { affiliate = {} } = result.data || {}

  if (!affiliateLoading) {
    if (isEmpty(affiliate)) setCookie = true
  }
  headerImage =
    get(affiliate, 'signIn.headerImage.0.url') ||
    urlHelmetData?.icon ||
    housefoliosLogo

  const [login, setLogin] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [passwordIsMasked, setPasswordIsMasked] = React.useState(true)
  const [loader, setLoader] = React.useState(false)
  const [formError, setFormError] = React.useState('')

  const [signIn] = useMutation(SIGN_IN)

  const onSubmit = (event, subscriptionPlan) => {
    event.preventDefault()
    setLoader(true)
    setFormError('')

    signIn({
      variables: {
        login,
        password,
      },
    })
      .then(async ({ data  }) => {
        if (data.signIn.reset) {
          navigate(`/resetpassword/${data.signIn.token}`)
        } else {
          saveAuthToken(data.signIn.token)
          localStorage.setItem('trialBanner', 'true')
          if (setCookie && data) {
            data.signIn.member &&
              (document.cookie = `SourceSignUp=${data.signIn.member}; expires=Wed, 31 Dec 2025 12:00:00 UTC;`)
          }
          window.location.replace('/')
        }
        setLoader(false)
      })
      .catch((error) => {
        setLoader(false)
        // Set a user-friendly error message based on the error type
        if (error.message?.includes('No user found')) {
          setFormError('No account found with this email address')
        } else if (error.message?.includes('Invalid password')) {
          setFormError('Incorrect password')
        } else if (error.message?.includes('deactivated')) {
          setFormError('Account is not active. Contact Support.')
        } else if (error.message?.includes('No organization found')) {
          setFormError(
            'No organization found for this account. Please contact support.',
          )
        } else {
          setFormError('Unable to sign in. Please try again.')
        }
        console.error('Sign in error:', error)
      })
  }

  const togglePasswordMask = () => {
    setPasswordIsMasked(!passwordIsMasked)
  }

  if (affiliateLoading)
    return (
      <Backdrop open={true} sx={authBackdropSx}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )

  return (
    <AuthPageShell>
      <AuthMobileLogo src={headerImage} />
      <Grid sx={authCardWrapSx}>
        <Card sx={authCardSx}>
          <CardContent>
            <Grid container direction="column" spacing={4}>
              <Grid>
                <AuthCardHeader
                  title="Welcome back"
                  subtitle="Use your credentials to sign in"
                  logoSrc={headerImage}
                />
              </Grid>

              {/* <Grid>
                    <Button
                      variant='outlined'
                      fullWidth={true}
                    >
                      <FontAwesomeIcon
                        style={{ marginRight: 10 }}
                        icon={['fab', 'google']}
                        size="1x"
                      />
                      <Typography>SIGN IN WITH GOOGLE</Typography>
                    </Button>
                  </Grid>
                  <Grid style={{ textAlign: 'center' }}>
                    <Typography>OR</Typography>
                  </Grid> */}
              <Grid>
                <BlockUi
                  blocking={loader}
                  loader={
                    <PacmanLoader
                      color={'var(--primary)'}
                      loading={loader}
                    />
                  }
                  message={
                    <Box sx={{ color: '#368cff' }}>
                      Verifying User
                    </Box>
                  }
                >
                  {formError && (
                    <Grid size={12} sx={authFormErrorWrapSx}>
                      <Card sx={authFormErrorCardSx}>
                        <ErrorOutlinedIcon sx={authFormErrorIconSx} />
                        <Typography sx={authFormErrorTextSx}>
                          {formError}
                        </Typography>
                      </Card>
                    </Grid>
                  )}
                  <ValidatorForm
                    onSubmit={(event) => onSubmit(event)}
                  >
                    <Grid container direction="row" spacing={2}>
                      <Grid size={12}>
                        <TextValidator
                          variant="outlined"
                          label="Email Address"
                          fullWidth={true}
                          value={login}
                          onChange={(event) => {
                            setLogin(event.target.value)
                            setFormError('') // Clear error on change
                          }}
                          autoFocus
                          type="text"
                          error={formError}
                          helperText={
                            formError?.includes(
                              'No account found with this email',
                            )
                              ? 'No account found with this email'
                              : ''
                          }
                          validators={[
                            'required',
                            'isEmail',
                            EMAIL_MAX_LENGTH_VALIDATOR,
                          ]}
                          errorMessages={[
                            'Email is required',
                            'Please enter a valid email address',
                            EMAIL_MAX_LENGTH_MESSAGE,
                          ]}
                        />
                      </Grid>
                      <Grid size={12}>
                        <TextValidator
                          variant="outlined"
                          label="Password"
                          value={password}
                          onChange={(event) => {
                            setPassword(event.target.value)
                            setFormError('') // Clear error on change
                          }}
                          fullWidth={true}
                          type={
                            passwordIsMasked ? 'password' : 'text'
                          }

                          error={formError}
                          helperText={
                            formError?.includes('Incorrect password')
                              ? 'Incorrect password'
                              : ''
                          }
                          validators={[
                            'required',
                            'maxStringLength: 50',
                          ]}
                          errorMessages={[
                            'Password is required',
                            'Password cannot exceed 50 characters',
                          ]}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={togglePasswordMask}
                                    size="small"
                                  >
                                    <Avatar sx={authPasswordToggleAvatarSx}>
                                      {passwordIsMasked ? (
                                        <RemoveRedEyeIcon />
                                      ) : (
                                        <VisibilityOffIcon />
                                      )}
                                    </Avatar>
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={12}>
                        <AuthPrimaryButton type="submit">
                          Sign in
                        </AuthPrimaryButton>
                      </Grid>
                      <Grid size={12}>
                        <Grid
                          container
                          direction="row"
                          wrap="nowrap"
                          sx={{
                            alignItems: 'center',
                          }}
                        >
                          {!urlAffiliate && (
                            <Grid>
                              <AuthNavLink
                                href={
                                  hostName === 'localhost' ||
                                  hostName ===
                                    'dev-admin.housefolios.com' ||
                                  hostName ===
                                    'dev-admin-beta.housefolios.com'
                                    ? '/createAccount'
                                    : 'https://housefolios.com/pricing'
                                }
                              >
                                Create account
                              </AuthNavLink>
                            </Grid>
                          )}
                          <Typography sx={authFlexSpacerSx} />
                          <Grid sx={authTextEndSx}>
                            <AuthNavLink href="/ForgotPassword">
                              Forgot password
                            </AuthNavLink>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </ValidatorForm>
                </BlockUi>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </AuthPageShell>
  )
}
